import {createSlice} from '@reduxjs/toolkit'
import text from './../text.json'
import ResourceService from '../../api/resource'
import {setConfirmIsActive} from '../alert'
import localStorageManager from '../../utils/localStorageManager'
import FormHelper from '../../utils/forms'
import QueryHelper from '../../utils/query'
import localStorageKeys from '../../utils/localStorageKeys'
import {toast} from 'react-toastify'
import {doNothing} from '../../utils/placeholder'
import {getAllLimitUsages} from '../auth'
import {setActivePopup} from '../popup'

const CONDITIONS = 'conditions'
const setConditionsErrors = (fields, setFieldError, message, type) => {
    const fieldsKeys = fields && Object.keys(fields)?.filter(field => field !== CONDITIONS)
    fieldsKeys?.forEach(fieldKey => {
        setFieldError(fieldKey, fields[fieldKey])
    })
    fields?.conditions ? FormHelper.setFormElementError({
        [type]: fields?.conditions
    }, message, setFieldError) : FormHelper.setFormElementError(fields, message, setFieldError)
}

export const resourcesSlice = createSlice({
    name: 'resources',
    initialState: {
        buttonIsDisabled: false,
        showLoader: false,
        services: [],
        resources: [],
        selectedResources: [],
        serverResources: [],
        isFetching: true,
        isDeleting: false,
        isPausing: false,
        isMonitoring: false,
        isFirstLogin: false,
        isFirstResource: false
    },
    reducers: {
        setButtonIsDisabled: (state, {payload}) => {
            state.buttonIsDisabled = payload
        },
        setShowLoader: (state, {payload}) => {
            state.showLoader = payload
        },
        setServices: (state, {payload}) => {
            state.services = payload
        },
        setResources: (state, {payload}) => {
            state.resources = payload
        },
        setServerResources: (state, {payload}) => {
            state.serverResources = payload
        },
        setSelectedResource: (state, {payload}) => {
            state.selectedResources = payload
        },
        setIsFetching: (state, {payload}) => {
            state.isFetching = payload
        },
        setIsDeleting: (state) => {
            state.isDeleting = !state.isDeleting
        },
        setActionsStatus: (state, {payload}) => {
            state[payload] = !state[payload]
        },
        setIsFirstLogin: (state, {payload}) => {
            state.isFirstLogin = payload
        },
        setIsFirstResource: (state, {payload}) => {
            state.isFirstResource = payload
        }
    }
})

export const {
    setButtonIsDisabled,
    setShowLoader,
    setServices,
    setResources,
    setIsFetching,
    setSelectedResource,
    setIsDeleting,
    setActionsStatus,
    setIsFirstLogin,
    setIsFirstResource,
    setServerResources
} = resourcesSlice.actions

export default resourcesSlice.reducer

export const addResource = (formData, setFieldError, resetForm, setButtonIsDisabled) => {
    return async(dispatch, getState) => {
        const requestData = {...formData}

        if (!requestData.conditionOption?.values) {
            requestData.conditions = null
        }

        if (requestData.keyword && (requestData?.conditionOption?.isCondition || requestData?.conditionOption?.value)) {
            const {value: operator, type} = requestData.conditionOption
            requestData.conditions = [
                {
                    values: [requestData.keyword],
                    operator,
                    type
                }
            ]
        }

        if (requestData.conditionOption?.values) {
            requestData.conditions = requestData.conditions.map(condition => ({values: [condition.value], operator: condition.operator.value, key: condition.key, type: requestData.conditionOption?.type}))
        }

        delete requestData.keyword
        delete requestData.conditionOption
        delete requestData.operator
        delete requestData.key
        delete requestData.value
        setButtonIsDisabled(true)
        try {
            const {data} = await ResourceService.addResource(requestData)
            toast.success(data?.message)
            dispatch(getResources(false))
            dispatch(setActivePopup(null))
            dispatch(setIsFirstLogin(false))
            dispatch(getAllLimitUsages())
            resetForm()
            const createdResources = localStorageManager.get(localStorageKeys.CREATED_RESOURCES) || {}
            createdResources[data?.data?.resource?.id] = Date.now()
            localStorageManager.set(localStorageKeys.CREATED_RESOURCES, createdResources)
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            setConditionsErrors(fields, setFieldError, message, requestData?.conditions?.[0]?.type)
        } finally {
            setButtonIsDisabled(false)
        }
    }
}

export const getServices = (showLoader = false, returnData = false, id = null) => {
    return async(dispatch, getState) => {
        showLoader && dispatch(setIsFetching(true))
        try {
            const teamId = id || getState().teams.selectedTeamId
            const {data} = await ResourceService.getServices(teamId)
            const services = data?.data?.services.map(item => {
                return {
                    ...item,
                    value: item.service_id,
                    label: item.name
                }
            })
            if (!id) {
                localStorageManager.set('services', services)
                localStorage.setItem('serviceId', services[0].value)
                localStorage.setItem(localStorageKeys.TEAM_ID, getState().teams.selectedTeamId)
            }

            if (returnData) {
                return services
            }
            dispatch(setServices(services))
        } catch (e) {
            doNothing()
        } finally {
            showLoader && dispatch(setIsFetching(false))
        }
    }
}

export const getAllServices = teams => {
    return async() => {
        try {
            const promises = teams?.map(team => {
                return ResourceService.getServices(team?.team_id)
            })
            const res = await Promise.all(promises)
            const services = res?.map((item, index) => {
                return item?.data?.data?.services?.map(service => {
                    return {
                        ...service,
                        team_id: teams?.[index]?.team_id
                    }
                })
            })
            return services?.reduce((accumulator, currentArray) => {
                return accumulator.concat(currentArray)
            }, [])
        } catch (e) {
            doNothing()
        }
    }
}

export const getResources = (showLoader = false, isReturned = false) => {
    return async dispatch => {
        showLoader && dispatch(setIsFetching(true))
        try {
            const services = localStorageManager.get('services')
            let response = await Promise.all(services?.map(service => ResourceService.getResources(service?.value)))
            response = response?.map(item => {
                return item?.data?.data?.resources
            })
            let resources = []
            response?.map(item => {
                item && resources.push(item)
            })
            showLoader && dispatch(setIsFetching(false))

            if (isReturned) {
                return resources?.flat(1) || []
            }
            dispatch(setResources(resources?.flat(1) || []))
            showLoader && dispatch(setIsFetching(false))
        } catch {
            doNothing()
        }
    }
}

export const getAllServicesEnabledResources = services => {
    return async() => {
        try {
            const promises = services?.map(service => {
                return ResourceService.getResources(service?.service_id)
            })
            const res = await Promise.all(promises)
            return res?.map(item => {
                return {
                    resources: item?.data?.data?.resources,
                    service_id: item?.data?.data?.resources?.[0]?.service_id
                }
            })
        } catch (e) {
            doNothing()
        }
    }
}

export const getServiceResources = (id, returnData = false) => {
    return async dispatch => {
        try {
            const {data} = await ResourceService.getResources(id)
            const resources = data?.data?.resources
            dispatch(setResources(resources))
            if (returnData) {
                return resources
            }
        } catch (e) {
            doNothing()
        }
    }
}

export const updateResource = (id, formData, setFieldError) => {
    return async(dispatch, getState) => {
        const requestData = {...formData}
        if (requestData.keyword && requestData?.conditionOption?.isCondition) {
            const {value: operator, type} = requestData.conditionOption
            requestData.conditions = [
                {
                    values: [requestData.keyword],
                    operator,
                    type
                }
            ]
        }

        if (!requestData?.conditionOption?.isCondition) {
            requestData.conditions = null
        }

        if (requestData?.conditionOption?.values) {
            requestData.conditions = formData.conditions.map(condition => ({values: [condition.value], operator: condition.operator.value, key: condition.key, type: requestData.conditionOption?.type}))
        }

        delete requestData.keyword
        delete requestData.conditionOption
        delete requestData.operator
        delete requestData.key
        delete requestData.value
        dispatch(setShowLoader(true))
        try {
            const {data} = await ResourceService.updateResource(id, requestData)
            dispatch(getResources(false))
            dispatch(setActivePopup(null))
            toast.success(data?.message)
            return true
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            setConditionsErrors(fields, setFieldError, message, requestData?.conditions?.[0]?.type)
        }
        dispatch(setShowLoader(false))
    }
}

export const deleteResources = ids => {
    return async(dispatch, getState) => {
        dispatch(setIsDeleting())
        try {
            const query = QueryHelper.makeQueryFromArray('id', ids)
            const {data} = await ResourceService.deleteResource(query)
            dispatch(getResources())
            dispatch(setSelectedResource([]))
            dispatch(getAllLimitUsages())
            toast.success(data?.message)
            !data?.data.all_deleted && toast.error(text.try_again)
            return true
        } catch (e) {
            toast.error(e?.response?.data?.message)
        } finally {
            dispatch(setIsDeleting())
            dispatch(setConfirmIsActive(false))
        }
    }
}

export const addService = (formData, setFieldError, closeModal) => {
    return async dispatch => {
        dispatch(setShowLoader(true))
        try {
            const {data} = await ResourceService.addService(formData)
            toast.success(data?.message)
            dispatch(getServices())
            dispatch(getAllLimitUsages())
            closeModal()
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
        dispatch(setShowLoader(false))
    }
}

export const updateService = (id, formData, setFieldError, closeModal) => {
    return async dispatch => {
        dispatch(setShowLoader(true))
        try {
            const {data} = await ResourceService.updateService(id, formData)
            toast.success(data?.message)
            dispatch(getServices())
            closeModal()
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError({keyword: fields.conditions, ...fields}, message, setFieldError)
        }
        dispatch(setShowLoader(false))
    }
}

export const deleteService = id => {
    return async dispatch => {
        dispatch(setIsDeleting())
        try {
            const {data} = await ResourceService.deleteService(id)
            toast.success(data?.message)
            dispatch(getServices())
            dispatch(setIsDeleting())
            dispatch(getAllLimitUsages())
            return true
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message)
        }
        dispatch(setIsDeleting())
    }
}

export const toggleResourcesStatus = (ids, statusData) => {
    const {enabled} = statusData
    let actionType = enabled ? 'isMonitoring' : 'isPausing'
    return async(dispatch, getState) => {
        dispatch(setActionsStatus(actionType))
        try {
            const query = QueryHelper.makeQueryFromArray('id', ids)
            const {data} = ids?.length > 1 ?
                await ResourceService.toggleResourcesStatus(query, statusData) :
                await ResourceService.toggleMonitoringStatus(ids?.[0], statusData)
            dispatch(getResources(false))
            dispatch(getAllLimitUsages())
            toast.success(data?.message)
        } catch (e) {
            toast.error(e?.response?.data?.message)
        }
        dispatch(setActionsStatus(actionType))
    }
}

export const getServerResources = (showLoader = false, isReturned = false) => {
    return async(dispatch) => {
        showLoader && dispatch(setIsFetching(true))
        try {
            const services =  localStorageManager.get('services')
            const response = await Promise.all(services?.map(service => ResourceService.getServerResources(service?.value)))
            let resources = []
            response?.map(res => {
                if (res?.data?.data) {
                    resources.push(res?.data?.data?.server_resources)
                }
            })
            if (isReturned) {
                return resources.flat(1)
            }
            dispatch(setServerResources(resources.flat(1)))
        } catch {
            doNothing()
        } finally {
            showLoader && dispatch(setIsFetching(false))
        }
    }
}

export const deleteServerResources = ids => {
    return async(dispatch) => {
        dispatch(setIsDeleting(true))
        try {
            const query = QueryHelper.makeQueryFromArray('id', ids)
            const {data} = await ResourceService.deleteServerResource(query)
            dispatch(getServerResources())
            toast.success(data?.message)
            !data?.data.all_deleted && toast.error(text.try_again)
        } catch (e) {
            toast.error(e?.response?.data?.message)
        } finally {
            dispatch(setIsDeleting(false))
            dispatch(setConfirmIsActive(false))
        }
    }
}
